import React, { useState } from 'react';
import './CashPopup.css';
import { apiPatch } from '../../utils/api';

// Popup for the consolidated "Cash" card: lists the zero-balance cash accounts
// that are grouped on the dashboard. Each account can be topped up inline —
// once its balance is non-zero it automatically reappears as its own card.

function fmtEur(value) {
  if (value == null || isNaN(value)) return '—';
  return `€${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// EUR-per-unit FX for a given currency (same source as InvestmentList's edit flow)
async function fetchFxToEur(currency) {
  if (!currency || currency === 'EUR') return 1;
  const response = await fetch('https://api.fxratesapi.com/latest');
  const data = await response.json();
  const eurRate = data.rates.EUR;
  if (currency === 'GBP') return eurRate / data.rates.GBP;
  if (currency === 'USD') return eurRate;
  return eurRate / data.rates[currency];
}

function CashPopup({ onClose, accounts, onDataUpdate }) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = (acc) => {
    setEditingId(acc._id);
    setEditValue(String(acc.value ?? 0));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setError(null);
  };

  const saveEdit = async (acc) => {
    const numeric = Number(editValue);
    if (Number.isNaN(numeric) || numeric < 0) {
      setError('Enter a valid non-negative number');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fx = await fetchFxToEur(acc.currency);
      await apiPatch(`/api/investments/updateValue/${acc._id}`, {
        value: numeric,
        eurValue: Number((numeric * fx).toFixed(2)),
      });
      cancelEdit();
      if (onDataUpdate) onDataUpdate();
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content cash-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="cash-header">
          <h2>Cash — empty accounts</h2>
          <button className="cash-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="cash-intro">
          These accounts currently hold a zero balance, so they are grouped under one
          card on the dashboard. Update a balance below and the account will reappear
          as its own card.
        </div>

        <div className="cash-table">
          {accounts.map((acc) => (
            <div className="cash-row" key={acc._id}>
              <div className="cash-row-main">
                <span className="cash-name">{acc.name}</span>
                <span className="cash-sub">
                  {acc.subType || ''}{acc.subType ? ' · ' : ''}last updated {formatDateLong(acc.timeOfUpdate)}
                </span>
              </div>
              {editingId === acc._id ? (
                <div className="cash-edit">
                  <span className="cash-ccy">{acc.currency}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="cash-edit-input"
                    autoFocus
                  />
                  <button className="cash-btn cash-btn--save" disabled={saving} onClick={() => saveEdit(acc)}>
                    {saving ? '…' : 'Save'}
                  </button>
                  <button className="cash-btn" disabled={saving} onClick={cancelEdit}>Cancel</button>
                </div>
              ) : (
                <div className="cash-row-value">
                  <span className="cash-amount">
                    {acc.currency !== 'EUR' ? `${acc.currency} ${Number(acc.value || 0).toLocaleString()} · ` : ''}
                    {fmtEur(acc.eurValue || 0)}
                  </span>
                  <button className="cash-btn" onClick={() => startEdit(acc)}>Update</button>
                </div>
              )}
            </div>
          ))}
          {accounts.length === 0 && (
            <div className="cash-empty">No zero-balance accounts right now.</div>
          )}
        </div>

        {error && <div className="cash-error">{error}</div>}

        <div className="cash-foot">
          Balances are manual for now — a bank-feed integration (Ponto / Revolut API) is on the roadmap.
        </div>
      </div>
    </div>
  );
}

export default CashPopup;
